import { Fields, type IFIXParser, MDEntryType, type Message, Messages } from 'fixparser';

import type { MarketDataEntry } from '../schemas/marketData';

function getEnumValue(enumObj: any, name: string): string {
    return enumObj[name] || name;
}

export type PendingRequests = Map<string, (data: Message) => void>;

export type MarketDataPrices = Map<string, MarketDataEntry[]>;

export function handleMessage(
    message: Message,
    parser: IFIXParser,
    pendingRequests: PendingRequests,
    marketDataPrices: MarketDataPrices,
    maxPriceHistory: number,
    onPriceUpdate?: (symbol: string, data: MarketDataEntry) => void,
): void {
    void parser; // Parser unused for now

    const msgType = message.messageType;
    if (msgType === Messages.MarketDataSnapshotFullRefresh || msgType === Messages.MarketDataIncrementalRefresh) {
        const symbol = message.getField(Fields.Symbol)?.value as string;
        const fixJson = message.toFIXJSON();
        const entries = (fixJson.Body?.NoMDEntries || []) as Array<{
            MDEntryType: string;
            MDEntryPx?: string;
            MDEntrySize?: string;
            MDUpdateAction?: string;
        }>;

        const data: MarketDataEntry = {
            timestamp: Date.now(),
            bid: 0,
            offer: 0,
            spread: 0,
            volume: 0,
            trade: 0,
            indexValue: 0,
            openingPrice: 0,
            closingPrice: 0,
            settlementPrice: 0,
            tradingSessionHighPrice: 0,
            tradingSessionLowPrice: 0,
            vwap: 0,
            imbalance: 0,
            openInterest: 0,
            compositeUnderlyingPrice: 0,
            simulatedSellPrice: 0,
            simulatedBuyPrice: 0,
            marginRate: 0,
            midPrice: 0,
            emptyBook: 0,
            settleHighPrice: 0,
            settleLowPrice: 0,
            priorSettlePrice: 0,
            sessionHighBid: 0,
            sessionLowOffer: 0,
            earlyPrices: 0,
            auctionClearingPrice: 0,
            swapValueFactor: 0,
            dailyValueAdjustmentForLongPositions: 0,
            cumulativeValueAdjustmentForLongPositions: 0,
            dailyValueAdjustmentForShortPositions: 0,
            cumulativeValueAdjustmentForShortPositions: 0,
            fixingPrice: 0,
            cashRate: 0,
            recoveryRate: 0,
            recoveryRateForLong: 0,
            recoveryRateForShort: 0,
            marketBid: 0,
            marketOffer: 0,
            shortSaleMinPrice: 0,
            previousClosingPrice: 0,
            thresholdLimitPriceBanding: 0,
            dailyFinancingValue: 0,
            accruedFinancingValue: 0,
            twap: 0,
        };

        for (const entry of entries) {
            const entryType = entry.MDEntryType;
            const price = entry.MDEntryPx ? Number.parseFloat(entry.MDEntryPx) : 0;
            const size = entry.MDEntrySize ? Number.parseFloat(entry.MDEntrySize) : 0;

            const enumValue = getEnumValue(MDEntryType, entryType);
            switch (enumValue) {
                case MDEntryType.Bid:
                    data.bid = price;
                    break;
                case MDEntryType.Offer:
                    data.offer = price;
                    break;
                case MDEntryType.Trade:
                    data.trade = price;
                    break;
                case MDEntryType.IndexValue:
                    data.indexValue = price;
                    break;
                case MDEntryType.OpeningPrice:
                    data.openingPrice = price;
                    break;
                case MDEntryType.ClosingPrice:
                    data.closingPrice = price;
                    break;
                case MDEntryType.SettlementPrice:
                    data.settlementPrice = price;
                    break;
                case MDEntryType.TradingSessionHighPrice:
                    data.tradingSessionHighPrice = price;
                    break;
                case MDEntryType.TradingSessionLowPrice:
                    data.tradingSessionLowPrice = price;
                    break;
                case MDEntryType.VWAP:
                    data.vwap = price;
                    break;
                case MDEntryType.Imbalance:
                    data.imbalance = size;
                    break;
                case MDEntryType.TradeVolume:
                    data.volume = size;
                    break;
                case MDEntryType.OpenInterest:
                    data.openInterest = size;
                    break;
                case MDEntryType.CompositeUnderlyingPrice:
                    data.compositeUnderlyingPrice = price;
                    break;
                case MDEntryType.SimulatedSellPrice:
                    data.simulatedSellPrice = price;
                    break;
                case MDEntryType.SimulatedBuyPrice:
                    data.simulatedBuyPrice = price;
                    break;
                case MDEntryType.MarginRate:
                    data.marginRate = price;
                    break;
                case MDEntryType.MidPrice:
                    data.midPrice = price;
                    break;
                case MDEntryType.EmptyBook:
                    data.emptyBook = 1;
                    break;
                case MDEntryType.SettleHighPrice:
                    data.settleHighPrice = price;
                    break;
                case MDEntryType.SettleLowPrice:
                    data.settleLowPrice = price;
                    break;
                case MDEntryType.PriorSettlePrice:
                    data.priorSettlePrice = price;
                    break;
                case MDEntryType.SessionHighBid:
                    data.sessionHighBid = price;
                    break;
                case MDEntryType.SessionLowOffer:
                    data.sessionLowOffer = price;
                    break;
                case MDEntryType.EarlyPrices:
                    data.earlyPrices = price;
                    break;
                case MDEntryType.AuctionClearingPrice:
                    data.auctionClearingPrice = price;
                    break;
                case MDEntryType.SwapValueFactor:
                    data.swapValueFactor = price;
                    break;
                case MDEntryType.DailyValueAdjustmentForLongPositions:
                    data.dailyValueAdjustmentForLongPositions = price;
                    break;
                case MDEntryType.CumulativeValueAdjustmentForLongPositions:
                    data.cumulativeValueAdjustmentForLongPositions = price;
                    break;
                case MDEntryType.DailyValueAdjustmentForShortPositions:
                    data.dailyValueAdjustmentForShortPositions = price;
                    break;
                case MDEntryType.CumulativeValueAdjustmentForShortPositions:
                    data.cumulativeValueAdjustmentForShortPositions = price;
                    break;
                case MDEntryType.FixingPrice:
                    data.fixingPrice = price;
                    break;
                case MDEntryType.CashRate:
                    data.cashRate = price;
                    break;
                case MDEntryType.RecoveryRate:
                    data.recoveryRate = price;
                    break;
                case MDEntryType.RecoveryRateForLong:
                    data.recoveryRateForLong = price;
                    break;
                case MDEntryType.RecoveryRateForShort:
                    data.recoveryRateForShort = price;
                    break;
                case MDEntryType.MarketBid:
                    data.marketBid = price;
                    break;
                case MDEntryType.MarketOffer:
                    data.marketOffer = price;
                    break;
                case MDEntryType.ShortSaleMinPrice:
                    data.shortSaleMinPrice = price;
                    break;
                case MDEntryType.PreviousClosingPrice:
                    data.previousClosingPrice = price;
                    break;
                case MDEntryType.ThresholdLimitPriceBanding:
                    data.thresholdLimitPriceBanding = price;
                    break;
                case MDEntryType.DailyFinancingValue:
                    data.dailyFinancingValue = price;
                    break;
                case MDEntryType.AccruedFinancingValue:
                    data.accruedFinancingValue = price;
                    break;
                case MDEntryType.TWAP:
                    data.twap = price;
                    break;
            }
        }

        data.spread = data.offer - data.bid;

        if (!marketDataPrices.has(symbol)) {
            marketDataPrices.set(symbol, []);
        }

        const prices = marketDataPrices.get(symbol)!;
        prices.push(data);

        // Keep only the last maxPriceHistory entries
        if (prices.length > maxPriceHistory) {
            prices.splice(0, prices.length - maxPriceHistory);
        }

        onPriceUpdate?.(symbol, data);

        // Resolve the market data request promise if it exists
        const mdReqID = message.getField(Fields.MDReqID)?.value as string;
        if (mdReqID) {
            const callback = pendingRequests.get(mdReqID);
            if (callback) {
                callback(message);
                pendingRequests.delete(mdReqID);
            }
        }
    } else if (msgType === Messages.ExecutionReport) {
        const reqId = message.getField(Fields.ClOrdID)?.value as string;
        const callback = pendingRequests.get(reqId);
        if (callback) {
            callback(message);
            pendingRequests.delete(reqId);
        }
    }
}
